/**
 * Wiring this product to the Refract feedback hub (feedback-hub INTEGRATION.md).
 *
 * The hub replaces the native feedback path (decided 2026-08-11, STATUS §19):
 * one widget in every Refract product, one hub instead of twelve inboxes, and
 * the hard-won client behaviour — outbox, retry, redaction, clamped
 * diagnostics, local wall-clock timestamps — maintained once in
 * `@refractspaces/feedback-widget` rather than re-fixed per product.
 *
 * **The key is the switch.** With `VITE_FEEDBACK_PROJECT_KEY` set the hub
 * widget renders and the native button does not; without it the native path
 * keeps working exactly as it always has, so feedback never goes dark while
 * the key is being issued. The key is publishable by design — origin-locked
 * and rate-limited at the hub, and the only thing it can do is file a report.
 *
 * The mapping functions are pure and tested, because two of the hub's rules
 * are the kind that fail silently: an invented email address sends a stranger
 * someone else's report, and a UUID where a sale number belongs makes every
 * report cost a database lookup to read (STATUS §19: "the register and sale by
 * name rather than by UUID").
 */

export function feedbackProjectKey(): string | null {
  const key = import.meta.env.VITE_FEEDBACK_PROJECT_KEY?.trim();
  return key === undefined || key === '' ? null : key;
}

export interface SessionEmployee {
  readonly id: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly role: string;
}

/**
 * Who is reporting, in the hub's shape.
 *
 * `email` passes through as-is — **null is expected, never invented**
 * (INTEGRATION §3.1). Staff sign in with a PIN and many have no address on
 * file; the hub's fallback chain answers them through the project admin. A
 * plausible-looking invented address is worse than none: it silently mails a
 * stranger the report.
 */
export function toReporter(employee: SessionEmployee | null | undefined) {
  if (employee == null) {
    return { externalId: null, name: null, email: null, role: null };
  }
  return {
    externalId: employee.id,
    name: employee.displayName,
    email: employee.email ?? null,
    role: employee.role,
  };
}

export interface FeedbackViewContext {
  readonly view: string;
  /** The document the operator is looking at, by its printed number — never a UUID. */
  readonly saleNumber?: string | null;
  /** Where they are standing — `Register 1`, the reference a triager can walk to. */
  readonly registerName?: string | null;
}

/**
 * What they were looking at when they pressed the button.
 *
 * `recordRef` is the sale **number** (`S-100042`) rather than its id: a report
 * is read by a person, and "which sale?" answered with a UUID is answered with
 * a database query. The old native path resolved ids to names on the server;
 * the hub has no access to this product's database, so the name has to be sent.
 *
 * **A sale in progress has no number** — one is allocated when it completes,
 * so a discarded cart never burns a receipt number (decision 84). Mid-sale the
 * register's name is the reference, which is what a person triaging "the cart
 * emptied on me" actually needs anyway.
 */
export function toContext(context: FeedbackViewContext) {
  return {
    view: context.view,
    recordRef: context.saleNumber ?? context.registerName ?? null,
  };
}
